import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

interface Ticket {
  id: number;
  ticket_id?: string;
  company_id: number;
  company_name: string;
  company_email: string;
  user_id?: number;
  user_email?: string;
  created_by?: number;
  created_by_email?: string;
  last_updated_by?: number;
  last_updated_by_email?: string;
  last_updated_by_company_id?: number;
  assigned_to?: number;
  assigned_to_email?: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
}

interface TicketReply {
  id: number;
  ticket_id: number;
  user_id: number;
  user_email: string;
  user_role: string;
  user_company_id?: number;
  message: string;
  type: 'reply' | 'agent_reply' | 'internal';
  created_at: string;
}

const Tickets: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'all' | 'overview'>('all');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [replies, setReplies] = useState<TicketReply[]>([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyType, setReplyType] = useState<'reply' | 'agent_reply' | 'internal'>('agent_reply');
  const [showReplyModal, setShowReplyModal] = useState(false);
  
  // Sorting and filtering state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    company: '',
    search: '',
  });
  const [copiedTicketId, setCopiedTicketId] = useState<string | null>(null);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  
  const isGlobalAdmin = user?.role === 'admin' && !user?.company_id;
  const isGlobalUser = !user?.company_id; // Global user (admin or customer with no company_id)
  const isAgent = isGlobalAdmin || isGlobalUser;
  const isCompanyAdmin = user?.role === 'admin' && user?.company_id;

  // Column visibility state with localStorage persistence
  const getDefaultColumns = () => ({
    ticketId: true,
    subject: true,
    company: isGlobalAdmin || isGlobalUser,
    user: true,
    status: true,
    priority: true,
    created: true,
    actions: true,
  });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('ticketColumns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const defaults = getDefaultColumns();
        return { ...defaults, ...parsed };
      } catch (e) {
        return getDefaultColumns();
      }
    }
    return getDefaultColumns();
  });

  // Update column visibility when user changes (to handle company column default)
  // Only update if there's no saved preference for company column
  useEffect(() => {
    const saved = localStorage.getItem('ticketColumns');
    if (!saved) {
      const defaults = getDefaultColumns();
      setColumnVisibility(defaults);
    } else {
      // Ensure company column visibility matches user permissions
      setColumnVisibility((prev: typeof columnVisibility) => ({
        ...prev,
        company: (isGlobalAdmin || isGlobalUser) ? (prev.company ?? true) : false
      }));
    }
  }, [isGlobalAdmin, isGlobalUser]);

  const saveColumnVisibility = (newVisibility: typeof columnVisibility) => {
    setColumnVisibility(newVisibility);
    localStorage.setItem('ticketColumns', JSON.stringify(newVisibility));
  };

  const toggleColumn = (column: keyof typeof columnVisibility) => {
    const newVisibility = { ...columnVisibility, [column]: !columnVisibility[column] };
    saveColumnVisibility(newVisibility);
  };
  const [formData, setFormData] = useState({
    company_id: '',
    user_id: '',
    assigned_to: '',
    subject: '',
    description: '',
    priority: 'medium',
    status: 'open',
  });

  useEffect(() => {
    if (viewMode === 'overview') {
      fetchOverview();
    } else {
      fetchTickets();
    }
    if (isGlobalAdmin || isGlobalUser) {
      fetchCompanies();
      fetchAgents();
    }
  }, [viewMode]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets');
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets/overview');
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching ticket overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await api.get('/users/agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchCompanyUsers = async (companyId: string) => {
    if (!companyId) {
      setCompanyUsers([]);
      return;
    }
    try {
      const response = await api.get(`/companies/${companyId}/users`);
      setCompanyUsers(response.data);
    } catch (error) {
      console.error('Error fetching company users:', error);
      setCompanyUsers([]);
    }
  };

  const fetchReplies = async (ticketId: number) => {
    try {
      const response = await api.get(`/tickets/${ticketId}/replies`);
      setReplies(response.data);
    } catch (error) {
      console.error('Error fetching replies:', error);
      setReplies([]);
    }
  };

  const handleViewTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    fetchReplies(ticket.id);
    setShowReplyModal(true);
  };

  const handleAddReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket || !replyMessage.trim()) return;

    try {
      const payload: any = {
        message: replyMessage,
      };
      
      // Only agents can specify type, customers always use 'reply'
      if (isAgent) {
        payload.type = replyType;
      }
      // Backend will auto-detect type for non-agents, but we can be explicit
      else {
        payload.type = 'reply';
      }

      await api.post(`/tickets/${selectedTicket.id}/replies`, payload);
      setReplyMessage('');
      if (isAgent) {
        setReplyType('agent_reply');
      }
      fetchReplies(selectedTicket.id);
      // Refresh ticket list
      if (viewMode === 'overview') {
        fetchOverview();
      } else {
        fetchTickets();
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error adding reply');
    }
  };

  const handleCreate = () => {
    setEditingTicket(null);
    setFormData({
      company_id: '',
      user_id: '',
      assigned_to: '',
      subject: '',
      description: '',
      priority: 'medium',
      status: 'open',
    });
    setCompanyUsers([]);
    setShowModal(true);
  };

  const handleEdit = (ticket: Ticket) => {
    setEditingTicket(ticket);
    setFormData({
      company_id: ticket.company_id.toString(),
      user_id: ticket.user_id ? ticket.user_id.toString() : '',
      assigned_to: ticket.assigned_to ? ticket.assigned_to.toString() : '',
      subject: ticket.subject,
      description: ticket.description,
      priority: ticket.priority,
      status: ticket.status,
    });
    // Fetch company users if editing and user is global admin/user
    if ((isGlobalAdmin || isGlobalUser) && ticket.company_id) {
      fetchCompanyUsers(ticket.company_id.toString());
    }
    if (isGlobalAdmin || isGlobalUser) {
      fetchAgents();
    }
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      try {
        await api.delete(`/tickets/${id}`);
        fetchTickets();
      } catch (error) {
        console.error('Error deleting ticket:', error);
        alert('Error deleting ticket');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTicket) {
        const updatePayload: any = {
          subject: formData.subject,
          description: formData.description,
          priority: formData.priority,
          status: formData.status,
        };
        if (formData.assigned_to && (isGlobalAdmin || isGlobalUser)) {
          updatePayload.assigned_to = parseInt(formData.assigned_to);
        } else if (formData.assigned_to === '' && (isGlobalAdmin || isGlobalUser)) {
          updatePayload.assigned_to = null;
        }
        await api.put(`/tickets/${editingTicket.id}`, updatePayload);
      } else {
        const payload: any = {
          subject: formData.subject,
          description: formData.description,
          priority: formData.priority,
          status: formData.status,
        };
        
        // Only include company_id if it's a valid number (for global admin/user)
        if (formData.company_id && !isNaN(parseInt(formData.company_id))) {
          payload.company_id = parseInt(formData.company_id);
        }
        
        // Only include user_id if it's provided and valid
        if (formData.user_id && !isNaN(parseInt(formData.user_id))) {
          payload.user_id = parseInt(formData.user_id);
        }
        
        // Only include assigned_to if it's provided and valid (for global admin/user)
        if (formData.assigned_to && (isGlobalAdmin || isGlobalUser) && !isNaN(parseInt(formData.assigned_to))) {
          payload.assigned_to = parseInt(formData.assigned_to);
        }
        
        await api.post('/tickets', payload);
      }
      setShowModal(false);
      if (viewMode === 'overview') {
        fetchOverview();
      } else {
        fetchTickets();
      }
    } catch (error: any) {
      console.error('Error saving ticket:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.errors?.map((e: any) => e.msg).join(', ') || error.message || 'Error saving ticket';
      alert(errorMessage);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedAndFilteredTickets = () => {
    let filtered = [...tickets];

    // Apply filters
    if (filters.status) {
      filtered = filtered.filter(t => t.status === filters.status);
    }
    if (filters.priority) {
      filtered = filtered.filter(t => t.priority === filters.priority);
    }
    if (filters.company && (isGlobalAdmin || isGlobalUser)) {
      filtered = filtered.filter(t => 
        t.company_id?.toString() === filters.company ||
        t.company_name?.toLowerCase().includes(filters.company.toLowerCase()) ||
        t.company_email?.toLowerCase().includes(filters.company.toLowerCase())
      );
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(t =>
        t.subject.toLowerCase().includes(searchLower) ||
        t.description.toLowerCase().includes(searchLower) ||
        t.user_email?.toLowerCase().includes(searchLower) ||
        t.company_name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        // Handle joined fields
        if (sortColumn === 'company_name') {
          aVal = a.company_name || '';
          bVal = b.company_name || '';
        } else if (sortColumn === 'user_email') {
          aVal = a.user_email || '';
          bVal = b.user_email || '';
        } else {
          aVal = a[sortColumn as keyof Ticket];
          bVal = b[sortColumn as keyof Ticket];
        }

        // Handle date sorting
        if (sortColumn === 'created_at') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = (bVal || '').toLowerCase();
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by created_at desc
      filtered.sort((a, b) => {
        const aTime = new Date(a.created_at).getTime();
        const bTime = new Date(b.created_at).getTime();
        return bTime - aTime;
      });
    }

    return filtered;
  };

  const sortedAndFilteredTickets = getSortedAndFilteredTickets();

  const copyToClipboard = async (text: string, ticketId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTicketId(ticketId);
      setTimeout(() => setCopiedTicketId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedTicketId(ticketId);
        setTimeout(() => setCopiedTicketId(null), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortColumn !== column) {
      return (
        <span className="ml-1 text-gray-400">
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        </span>
      );
    }
    return (
      <span className="ml-1 text-indigo-600">
        {sortDirection === 'asc' ? (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Support Tickets</h1>
        <div className="flex items-center space-x-4">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                viewMode === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              All Tickets
            </button>
            <button
              onClick={() => setViewMode('overview')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md ${
                viewMode === 'overview'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Overview (Open)
            </button>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create Ticket
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search tickets..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          {(isGlobalAdmin || isGlobalUser) && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
              <input
                type="text"
                placeholder="Filter by company..."
                value={filters.company}
                onChange={(e) => setFilters({ ...filters, company: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: '', priority: '', company: '', search: '' })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Tickets</h3>
          <div className="relative">
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Columns
            </button>
            {showColumnMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowColumnMenu(false)}></div>
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-20 border border-gray-200 dark:border-gray-700">
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">Toggle Columns</div>
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={columnVisibility.ticketId}
                        onChange={() => toggleColumn('ticketId')}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Ticket ID</span>
                    </label>
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={columnVisibility.subject}
                        onChange={() => toggleColumn('subject')}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Subject</span>
                    </label>
                    {(isGlobalAdmin || isGlobalUser) && (
                      <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={columnVisibility.company}
                          onChange={() => toggleColumn('company')}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Company</span>
                      </label>
                    )}
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={columnVisibility.user}
                        onChange={() => toggleColumn('user')}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">User</span>
                    </label>
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={columnVisibility.status}
                        onChange={() => toggleColumn('status')}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Status</span>
                    </label>
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={columnVisibility.priority}
                        onChange={() => toggleColumn('priority')}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Priority</span>
                    </label>
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={columnVisibility.created}
                        onChange={() => toggleColumn('created')}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Created</span>
                    </label>
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={columnVisibility.actions}
                        onChange={() => toggleColumn('actions')}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Actions</span>
                    </label>
                    <div className="border-t border-gray-200 mt-1">
                      <button
                        onClick={() => {
                          const defaults = getDefaultColumns();
                          saveColumnVisibility(defaults);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Reset to Default
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {columnVisibility.ticketId && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ticket ID
                  </th>
                )}
                {columnVisibility.subject && (
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('subject')}
                  >
                    <div className="flex items-center">
                      Subject
                      <SortIcon column="subject" />
                    </div>
                  </th>
                )}
                {(isGlobalAdmin || isGlobalUser) && columnVisibility.company && (
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('company_name')}
                  >
                    <div className="flex items-center">
                      Company
                      <SortIcon column="company_name" />
                    </div>
                  </th>
                )}
                {columnVisibility.user && (
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('user_email')}
                  >
                    <div className="flex items-center">
                      User
                      <SortIcon column="user_email" />
                    </div>
                  </th>
                )}
                {columnVisibility.status && (
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      <SortIcon column="status" />
                    </div>
                  </th>
                )}
                {columnVisibility.priority && (
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('priority')}
                  >
                    <div className="flex items-center">
                      Priority
                      <SortIcon column="priority" />
                    </div>
                  </th>
                )}
                {columnVisibility.created && (
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Created
                      <SortIcon column="created_at" />
                    </div>
                  </th>
                )}
                {columnVisibility.actions && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {sortedAndFilteredTickets.length === 0 ? (
                <tr>
                  <td colSpan={
                    [columnVisibility.ticketId, columnVisibility.subject, 
                     (isGlobalAdmin || isGlobalUser) && columnVisibility.company, 
                     columnVisibility.user, columnVisibility.status, 
                     columnVisibility.priority, columnVisibility.created, 
                     columnVisibility.actions].filter(Boolean).length
                  } className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {tickets.length === 0 ? 'No tickets found' : 'No tickets match the current filters'}
                  </td>
                </tr>
              ) : (
                sortedAndFilteredTickets.map((ticket) => {
                  const ticketId = ticket.ticket_id || `TKT-${String(ticket.id).padStart(6, '0')}`;
                  return (
                    <tr key={ticket.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      {columnVisibility.ticketId && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/tickets/${ticket.id}`}
                              className="text-sm font-mono font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:underline"
                              title={`View ticket ${ticketId}`}
                            >
                              {ticketId}
                            </Link>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                copyToClipboard(ticketId, ticketId);
                              }}
                              className="text-gray-400 hover:text-indigo-600 focus:outline-none"
                              title="Copy Ticket ID to clipboard"
                            >
                              {copiedTicketId === ticketId ? (
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                      )}
                      {columnVisibility.subject && (
                        <td className="px-6 py-4">
                          <Link
                            to={`/tickets/${ticket.id}`}
                            className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:underline block"
                            title={`View ticket ${ticketId}`}
                          >
                            {ticket.subject}
                          </Link>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{ticket.description}</div>
                        </td>
                      )}
                      {(isGlobalAdmin || isGlobalUser) && columnVisibility.company && (
                        <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">{ticket.company_name || 'N/A'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{ticket.company_email}</div>
                        </td>
                      )}
                      {columnVisibility.user && (
                        <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">{ticket.user_email || 'N/A'}</div>
                        {ticket.created_by_email && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">by {ticket.created_by_email}</div>
                        )}
                        {ticket.assigned_to_email && (
                          <div className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">Assigned: {ticket.assigned_to_email}</div>
                        )}
                        </td>
                      )}
                      {columnVisibility.status && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </td>
                      )}
                      {columnVisibility.priority && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </span>
                        </td>
                      )}
                      {columnVisibility.created && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(ticket.created_at).toLocaleDateString()}
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(ticket.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                      )}
                      {columnVisibility.actions && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewTicket(ticket)}
                              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                            >
                              View
                            </button>
                            {(isGlobalAdmin || isCompanyAdmin) && (
                              <>
                                <button
                                  onClick={() => handleEdit(ticket)}
                                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                                >
                                  Edit
                                </button>
                                {isGlobalAdmin && (
                                  <button
                                    onClick={() => handleDelete(ticket.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Delete
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowModal(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {editingTicket ? 'Edit Ticket' : 'Create Ticket'}
                  </h3>
                  <div className="space-y-4">
                    {!editingTicket && (isGlobalAdmin || isGlobalUser) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company</label>
                        <select
                          required
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.company_id}
                          onChange={(e) => {
                            setFormData({ ...formData, company_id: e.target.value, user_id: '' });
                            fetchCompanyUsers(e.target.value);
                          }}
                        >
                          <option value="">Select a company</option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.company_name || company.name} ({company.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {!editingTicket && (isGlobalAdmin || isGlobalUser) && formData.company_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User (Optional)</label>
                        <select
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.user_id}
                          onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                        >
                          <option value="">Select a user (optional)</option>
                          {companyUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.email} ({user.role})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {(isGlobalAdmin || isGlobalUser) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Assign To Agent (Optional)</label>
                        <select
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.assigned_to}
                          onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                        >
                          <option value="">Unassigned</option>
                          {agents.map((agent) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.email} ({agent.role})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subject</label>
                      <input
                        type="text"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                      <textarea
                        required
                        rows={4}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
                        <select
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                        <select
                          className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-base font-medium text-white hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedTicket && (
        <div className="fixed z-20 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => {
                setShowReplyModal(false);
                setSelectedTicket(null);
                setReplies([]);
                setReplyMessage('');
              }}></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {selectedTicket.subject}
                    </h3>
                    <div className="mt-2 flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowReplyModal(false);
                      setSelectedTicket(null);
                      setReplies([]);
                      setReplyMessage('');
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Ticket Details */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  {(isGlobalAdmin || isGlobalUser) && (
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">Company:</span> {selectedTicket.company_name} ({selectedTicket.company_email})
                    </p>
                  )}
                  {selectedTicket.user_email && (
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">User:</span> {selectedTicket.user_email}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">Created:</span> {new Date(selectedTicket.created_at).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-700">{selectedTicket.description}</p>
                </div>

                {/* Replies Section */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Replies</h4>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {replies.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No replies yet</p>
                    ) : (
                      replies.map((reply) => {
                        const isInternal = reply.type === 'internal';
                        const isAgentReply = reply.type === 'agent_reply';
                        const isCustomerReply = reply.type === 'reply';
                        const isFromAgent = !reply.user_company_id; // Global user/agent

                        return (
                          <div
                            key={reply.id}
                            className={`p-4 rounded-lg border ${
                              isInternal
                                ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
                                : isAgentReply
                                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                                : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center space-x-2">
                                <span className={`text-sm font-medium ${
                                  isInternal
                                    ? 'text-gray-900 dark:text-yellow-100'
                                    : isAgentReply
                                    ? 'text-gray-900 dark:text-blue-100'
                                    : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                  {reply.user_email}
                                </span>
                                {isInternal && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100">
                                    Internal Note
                                  </span>
                                )}
                                {isAgentReply && !isInternal && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100">
                                    Agent
                                  </span>
                                )}
                                {isCustomerReply && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                    Customer
                                  </span>
                                )}
                              </div>
                              <span className={`text-xs ${
                                isInternal
                                  ? 'text-gray-600 dark:text-yellow-200'
                                  : isAgentReply
                                  ? 'text-gray-600 dark:text-blue-200'
                                  : 'text-gray-500 dark:text-gray-400'
                              }`}>
                                {new Date(reply.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className={`text-sm whitespace-pre-wrap ${
                              isInternal
                                ? 'text-gray-800 dark:text-yellow-50'
                                : isAgentReply
                                ? 'text-gray-800 dark:text-blue-50'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}>{reply.message}</p>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Add Reply Form */}
                <form onSubmit={handleAddReply} className="border-t dark:border-gray-700 pt-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {isAgent ? 'Add Reply or Internal Note' : 'Add Reply'}
                    </label>
                    {isAgent && (
                      <div className="mb-2">
                        <select
                          value={replyType}
                          onChange={(e) => setReplyType(e.target.value as 'reply' | 'agent_reply' | 'internal')}
                          className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="agent_reply">Agent Reply (Visible to Customer)</option>
                          <option value="internal">Internal Note (Agents Only)</option>
                        </select>
                      </div>
                    )}
                    <textarea
                      required
                      rows={4}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                    />
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowReplyModal(false);
                        setSelectedTicket(null);
                        setReplies([]);
                        setReplyMessage('');
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {replyType === 'internal' ? 'Add Internal Note' : 'Add Reply'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
